import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Check, X, Users, BarChart3, Image, Shield } from 'lucide-react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import { analyticsService } from '../services/analyticsService'
import { AnalyticsEvents } from '../types/analytics'

interface PricingTier {
  id: string
  name: string
  displayName: string
  monthlyPrice: number
  annualPrice: number
  annualDiscount: number
  trialDays: number
  popular?: boolean
  features: {
    socialAnalytics: boolean
    emailAnalytics: boolean
    mediaAnalysis: boolean | 'addon'
    competitors: boolean
  }
  limits: {
    users: number
    accounts: number
    posts: number
    competitors: number
    postRetention: string
  }
  support: string
  cta: {
    primary: string
    secondary?: string
  }
}

const pricingTiers: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    displayName: 'Starter Plan',
    monthlyPrice: 299,
    annualPrice: 2870,
    annualDiscount: 20,
    trialDays: 14,
    features: {
      socialAnalytics: true,
      emailAnalytics: true,
      mediaAnalysis: false,
      competitors: false
    },
    limits: {
      users: 3,
      accounts: 3,
      posts: 100,
      competitors: 0,
      postRetention: '90 days'
    },
    support: 'Email',
    cta: {
      primary: 'Start 14-Day Free Trial'
    }
  },
  {
    id: 'growth',
    name: 'Growth',
    displayName: 'Growth Plan',
    monthlyPrice: 1999,
    annualPrice: 16793,
    annualDiscount: 30,
    trialDays: 14,
    popular: true,
    features: {
      socialAnalytics: true,
      emailAnalytics: true,
      mediaAnalysis: 'addon',
      competitors: true
    },
    limits: {
      users: 5,
      accounts: 10,
      posts: 1000,
      competitors: 10,
      postRetention: '180 days'
    },
    support: 'Email',
    cta: {
      primary: 'Start 14-Day Free Trial'
    }
  },
  {
    id: 'professional',
    name: 'Professional',
    displayName: 'Professional Plan',
    monthlyPrice: 4999,
    annualPrice: 41991,
    annualDiscount: 30,
    trialDays: 30,
    features: {
      socialAnalytics: true,
      emailAnalytics: true,
      mediaAnalysis: true,
      competitors: true
    },
    limits: {
      users: 15,
      accounts: 25,
      posts: 3500,
      competitors: 25,
      postRetention: '1 year'
    },
    support: 'Priority',
    cta: {
      primary: 'Start 30-Day Free Trial'
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    displayName: 'Enterprise Plan',
    monthlyPrice: 0,
    annualPrice: 0,
    annualDiscount: 0,
    trialDays: 0,
    features: {
      socialAnalytics: true,
      emailAnalytics: true,
      mediaAnalysis: true,
      competitors: true
    },
    limits: {
      users: -1,
      accounts: -1,
      posts: -1,
      competitors: -1,
      postRetention: 'Unlimited'
    },
    support: 'Dedicated Account Manager',
    cta: {
      primary: 'Contact Sales'
    }
  }
]

const featuresList = [
  {
    key: 'socialAnalytics',
    label: 'Social Analytics',
    icon: BarChart3,
    description: 'Track performance across all social media platforms'
  },
  {
    key: 'emailAnalytics',
    label: 'Email Analytics',
    icon: Shield,
    description: 'Monitor email campaign performance and engagement'
  },
  {
    key: 'mediaAnalysis',
    label: 'Media Analysis',
    icon: Image,
    description: 'AI-powered analysis of image and video content'
  },
  {
    key: 'competitors',
    label: 'Competitor Tracking',
    icon: Users,
    description: 'Compare your performance against competitors'
  }
]

const faqs = [
  {
    question: 'Can I switch plans at any time?',
    answer:
      'Yes! You can upgrade or downgrade your plan at any time. Upgrades take effect immediately with prorated charges. Downgrades take effect at the end of your current billing period.'
  },
  {
    question: 'What happens when my free trial ends?',
    answer:
      'After your free trial ends, your subscription will automatically convert to a paid plan and your card will be charged. A credit card is required to start the trial, but you won\'t be charged until the trial period ends. You can cancel anytime during the trial period to avoid any charges.'
  },
  {
    question: 'Do you offer refunds?',
    answer:
      "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with our service, contact us within 30 days of your purchase for a full refund."
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor, Stripe. Enterprise customers can also arrange for invoicing.'
  },
  {
    question: 'Can I get a custom plan for my organization?',
    answer:
      'Absolutely! Enterprise customers can work with our team to create custom packages that fit their specific needs, including custom pricing, features, and support levels.'
  }
]

const PricingPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(
    'monthly'
  )
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  // Track page view on mount
  useEffect(() => {
    analyticsService.track(AnalyticsEvents.SUBSCRIPTION_PLAN_VIEWED, {
      page: 'pricing',
    })
  }, [])

  const handleSelectPlan = (tier: string, billing: 'monthly' | 'annual') => {
    // Map tier name to tier ID
    const tierIdMap: Record<string, number> = {
      starter: 1,
      growth: 2,
      professional: 3,
      enterprise: 4,
    };

    const tierId = tierIdMap[tier] || 1;

    // Track plan selection
    analyticsService.track(AnalyticsEvents.SUBSCRIPTION_PLAN_SELECTED, {
      tierId,
      tierName: tier,
      billingCycle: billing,
    })

    // Navigate to registration page with tier and billing cycle
    window.location.href = `/register?selectedTierId=${tierId}&billingCycle=${billing}`;
  }

  const handleContactSales = () => {
    // TODO: Integrate support ticket or email modal
    // Possibly link to /support or open contact form
    alert('TODO: Implement contact sales form')
  }

  const formatPrice = (price: number) => {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatLimit = (value: number | 'unlimited') => {
    if (value === 'unlimited' || value === -1) {
      return 'Unlimited'
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`
    }
    return value.toString()
  }

  return (
    <div className='min-h-screen bg-gray-50 py-8 sm:py-10 lg:py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-7xl mx-auto'>
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className='text-center mb-8 sm:mb-10 lg:mb-12'
        >
          <img
            src='/avi-logo-white-crop.png'
            alt='AVI by Athletiverse'
            className='h-12 sm:h-14 lg:h-16 mx-auto mb-4 sm:mb-6'
          />
          <h1 className='text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4'>
            Simple, Transparent Pricing
          </h1>
          <p className='text-base sm:text-lg text-gray-600 max-w-2xl mx-auto'>
            Choose the perfect plan for your team. All plans include a free
            trial with no charges during the trial period.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className='flex items-center justify-center mb-8 sm:mb-10 lg:mb-12'
        >
          <div className='flex items-center space-x-4 bg-white rounded-lg p-1 shadow-sm border border-gray-200'>
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-md text-sm sm:text-base font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-avi-primary text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-md text-sm sm:text-base font-medium transition-all flex items-center ${
                billingCycle === 'annual'
                  ? 'bg-avi-primary text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Annual
              <Badge variant='success' className='ml-2 text-xs'>
                Save 20-30%
              </Badge>
            </button>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20'>
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
            >
              <Card
                variant='elevated'
                padding='md'
                className={`h-full flex flex-col hover:shadow-avi-hover transition-all duration-300 ${
                  tier.popular
                    ? 'border-2 border-avi-primary relative pt-6'
                    : ''
                }`}
              >
                {tier.popular && (
                  <div className='absolute -top-3 left-1/2 transform -translate-x-1/2 z-10'>
                    <Badge
                      variant='success'
                      className='text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 whitespace-nowrap'
                    >
                      Most Popular
                    </Badge>
                  </div>
                )}

                {/* Tier Header */}
                <div className='text-center mb-4'>
                  <h3 className='text-lg sm:text-xl font-bold text-gray-900 mb-1'>
                    {tier.name}
                  </h3>
                  {tier.id === 'enterprise' ? (
                    <>
                      <div className='h-[36px] sm:h-[40px]' />
                      <p className='text-xs text-gray-500 mt-1'>
                        Tailored to your organization
                      </p>
                      <p className='text-xs text-avi-primary font-medium mt-1'>
                        Custom trial available
                      </p>
                    </>
                  ) : (
                    <>
                      <div className='flex items-baseline justify-center'>
                        <span className='text-2xl sm:text-3xl font-bold text-gray-900'>
                          $
                          {billingCycle === 'monthly'
                            ? formatPrice(tier.monthlyPrice)
                            : formatPrice(tier.annualPrice / 12)}
                        </span>
                        <span className='text-gray-600 ml-1 text-sm'>/mo</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className='text-xs text-gray-500 mt-1'>
                          ${formatPrice(tier.annualPrice)} billed annually
                        </p>
                      )}
                      <p className='text-xs text-avi-primary font-medium mt-1'>
                        {tier.trialDays}-day free trial
                      </p>
                    </>
                  )}
                </div>

                {/* Features List */}
                <div className='space-y-2 mb-4'>
                  {featuresList.map((feature) => {
                    const featureValue =
                      tier.features[feature.key as keyof typeof tier.features]
                    const isEnabled =
                      featureValue === true || featureValue === 'addon'
                    const isAddon = featureValue === 'addon'
                    const FeatureIcon = feature.icon

                    return (
                      <div
                        key={feature.key}
                        className={`flex items-center ${
                          isEnabled ? 'text-gray-700' : 'text-gray-400'
                        }`}
                      >
                        <div className='flex-shrink-0 mr-2'>
                          {isEnabled ? (
                            <Check className='h-4 w-4 text-avi-accent' />
                          ) : (
                            <X className='h-4 w-4 text-gray-300' />
                          )}
                        </div>
                        <div className='flex items-center flex-wrap gap-1'>
                          <FeatureIcon className='h-3.5 w-3.5' />
                          <span className='text-xs sm:text-sm'>
                            {feature.label}
                          </span>
                          {isAddon && (
                            <Badge variant='warning' className='text-[10px] px-1.5 py-0.5'>
                              +$399
                            </Badge>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Limits */}
                <div className='border-t border-gray-200 pt-4 mb-4'>
                  <h4 className='text-xs font-semibold text-gray-900 mb-2'>
                    Usage Limits
                  </h4>
                  <div className='space-y-1.5 text-xs text-gray-600'>
                    <div className='flex justify-between'>
                      <span>Users:</span>
                      <span className='font-medium text-gray-900'>
                        {tier.limits.users === -1 ? 'Unlimited' : tier.limits.users}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Accounts:</span>
                      <span className='font-medium text-gray-900'>
                        {tier.limits.accounts === -1 ? 'Unlimited' : tier.limits.accounts}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Posts/mo:</span>
                      <span className='font-medium text-gray-900'>
                        {formatLimit(tier.limits.posts)}
                      </span>
                    </div>
                    {(tier.limits.competitors > 0 || tier.limits.competitors === -1) && (
                      <div className='flex justify-between'>
                        <span>Competitors:</span>
                        <span className='font-medium text-gray-900'>
                          {tier.limits.competitors === -1 ? 'Unlimited' : tier.limits.competitors}
                        </span>
                      </div>
                    )}
                    <div className='flex justify-between'>
                      <span>Retention:</span>
                      <span className='font-medium text-gray-900'>
                        {tier.limits.postRetention}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Support:</span>
                      <span className='font-medium text-gray-900 text-right'>
                        {tier.support}
                      </span>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className='mt-auto'>
                  <Button
                    variant={
                      tier.popular
                        ? 'primary'
                        : tier.id === 'enterprise'
                        ? 'secondary'
                        : 'outline'
                    }
                    className='w-full text-xs sm:text-sm py-2'
                    onClick={() =>
                      tier.id === 'enterprise'
                        ? handleContactSales()
                        : handleSelectPlan(tier.id, billingCycle)
                    }
                  >
                    {tier.cta.primary}
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className='mb-12 sm:mb-16 lg:mb-20'
        >
          <h2 className='text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8'>
            Compare Features
          </h2>
          <Card variant='default' padding='none' className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gray-50 border-b border-gray-200'>
                <tr>
                  <th className='px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-900'>
                    Feature
                  </th>
                  {pricingTiers.map((tier) => (
                    <th
                      key={tier.id}
                      className='px-4 sm:px-6 py-4 text-center text-sm font-semibold text-gray-900'
                    >
                      {tier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {featuresList.map((feature) => (
                  <tr key={feature.key} className='hover:bg-gray-50'>
                    <td className='px-4 sm:px-6 py-4 text-sm text-gray-700'>
                      <div className='flex items-center'>
                        <feature.icon className='h-4 w-4 mr-2 text-gray-400' />
                        {feature.label}
                      </div>
                    </td>
                    {pricingTiers.map((tier) => (
                      <td
                        key={tier.id}
                        className='px-4 sm:px-6 py-4 text-center'
                      >
                        {tier.features[
                          feature.key as keyof typeof tier.features
                        ] ? (
                          <Check className='h-5 w-5 text-avi-accent mx-auto' />
                        ) : (
                          <X className='h-5 w-5 text-gray-300 mx-auto' />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className='mb-12'
        >
          <h2 className='text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8'>
            Frequently Asked Questions
          </h2>
          <div className='max-w-3xl mx-auto space-y-4'>
            {faqs.map((faq, index) => (
              <Card
                key={index}
                variant='default'
                padding='none'
                className='overflow-hidden'
              >
                <button
                  onClick={() =>
                    setExpandedFaq(expandedFaq === index ? null : index)
                  }
                  className='w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors'
                >
                  <span className='font-semibold text-gray-900 text-sm sm:text-base'>
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: expandedFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg
                      className='h-5 w-5 text-gray-400'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M19 9l-7 7-7-7'
                      />
                    </svg>
                  </motion.div>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: expandedFaq === index ? 'auto' : 0,
                    opacity: expandedFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.2 }}
                  className='overflow-hidden'
                >
                  <div className='px-6 pb-4 text-sm sm:text-base text-gray-600'>
                    {faq.answer}
                  </div>
                </motion.div>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.8 }}
          className='text-center space-y-4'
        >
          <div className='flex flex-wrap justify-center gap-4 sm:gap-8 text-sm text-gray-600'>
            <div className='flex items-center'>
              <Check className='h-4 w-4 text-avi-accent mr-2' />
              <span>Free Trial Available</span>
            </div>
            <div className='flex items-center'>
              <Check className='h-4 w-4 text-avi-accent mr-2' />
              <span>No Charges During Trial</span>
            </div>
            <div className='flex items-center'>
              <Check className='h-4 w-4 text-avi-accent mr-2' />
              <span>Cancel Anytime</span>
            </div>
          </div>
          <p className='text-xs sm:text-sm text-gray-500'>
            Need help choosing?{' '}
            <a href='#' className='text-avi-primary hover:underline'>
              Contact our team
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  )
}

export default PricingPage
